import { createFileRoute, Link } from "@tanstack/react-router";
import { absoluteUrl } from "@/lib/site";
import { useEffect, useState } from "react";
import { listConcepts, type CodexConcept } from "@/lib/codex";
import { breadcrumbs, ldScript, orgRef, pageNode, personRef, seoMeta } from "@/lib/schema";

export const Route = createFileRoute("/codex/")({
  head: () => ({
    ...seoMeta({
      title: "The KAfrequency Codex — Living Knowledge",
      description:
        "A living knowledge architecture connecting every concept, Gate, teaching, practice, book, and song across the KAfrequency ecosystem.",
      path: "/codex",
    }),
    scripts: [
      ldScript(
        pageNode({
          type: "CollectionPage",
          id: `${absoluteUrl("/codex")}#codex`,
          url: absoluteUrl("/codex"),
          name: "The KAfrequency Codex",
          description:
            "The living knowledge architecture of the 47 Gates and the wider KAfrequency canon.",
          about: [personRef(), orgRef()],
        }),
      ),
      ldScript(breadcrumbs([["Codex", "/codex"]])),
    ],
  }),
  component: CodexIndex,
});

const CLUSTER_ORDER: string[] = [
  "foundations",
  "body",
  "senses",
  "mind",
  "spirit",
  "relations",
  "creation",
  "nature",
];

const CLUSTER_LABEL: Record<string, string> = {
  foundations: "Foundations",
  body: "Body",
  senses: "Senses",
  mind: "Mind",
  spirit: "Spirit",
  relations: "Relations",
  creation: "Creation",
  nature: "Nature",
};

function CodexIndex() {
  const [concepts, setConcepts] = useState<CodexConcept[]>([]);

  useEffect(() => {
    listConcepts()
      .then(setConcepts)
      .catch(() => setConcepts([]));
  }, []);

  const grouped = new Map<string, CodexConcept[]>();
  for (const c of concepts) {
    const key = c.cluster ?? "other";
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push(c);
  }

  const orderedClusters = [
    ...CLUSTER_ORDER.filter((k) => grouped.has(k)),
    ...Array.from(grouped.keys()).filter((k) => !CLUSTER_ORDER.includes(k)),
  ];

  return (
    <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24">
      <header className="mx-auto max-w-3xl text-center">
        <p className="text-[10px] uppercase tracking-[0.4em] text-gold">The Codex</p>
        <h1 className="mt-4 font-serif text-4xl leading-tight sm:text-6xl">
          A living knowledge architecture.<br />
          <span className="text-gold-luminous">Every idea, connected.</span>
        </h1>
        <p className="mt-6 text-base text-muted-foreground sm:text-lg">
          The KAfrequency Codex is the knowledge backbone of the ecosystem — a lattice
          of concepts that link the 47 Gates, music, teachings, books, practices, and
          journal prompts into one continuous field of study.
        </p>
      </header>

      {concepts.length === 0 && (
        <p className="mt-16 text-center text-sm text-muted-foreground">
          The Codex is being assembled…
        </p>
      )}

      <div className="mt-16 space-y-14">
        {orderedClusters.map((cluster) => {
          const list = grouped.get(cluster) ?? [];
          return (
            <section key={cluster} aria-labelledby={`cluster-${cluster}`}>
              <div className="flex items-baseline justify-between border-b hairline pb-3">
                <h2 id={`cluster-${cluster}`} className="font-serif text-2xl text-gold-luminous">
                  {CLUSTER_LABEL[cluster] ?? cluster}
                </h2>
                <span className="text-[10px] uppercase tracking-[0.3em] text-gold">
                  {list.length} concept{list.length === 1 ? "" : "s"}
                </span>
              </div>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {list.map((c) => (
                  <li key={c.slug}>
                    <Link
                      to="/codex/$slug"
                      params={{ slug: c.slug }}
                      className="group flex h-full flex-col gap-2 rounded-xl border hairline bg-obsidian/60 p-5 backdrop-blur transition-all hover:border-gold hover:bg-obsidian/80"
                    >
                      <span className="text-[10px] uppercase tracking-[0.3em] text-gold">
                        Concept
                      </span>
                      <span className="font-serif text-lg text-ivory group-hover:text-gold-luminous">
                        {c.title}
                      </span>
                      {c.essence && (
                        <span className="line-clamp-3 text-xs text-muted-foreground">
                          {c.essence}
                        </span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>
    </div>
  );
}
